import React, { useState, useEffect, useCallback, useMemo } from 'react';
import apiService from '../services/api';
import { useToast } from '../hooks/useToast';
import Toast from './Toast';
import ProgrammeHeroCard from './fidelite/ProgrammeHeroCard';
import RecompenseProgrammeMirror from './fidelite/RecompenseProgrammeMirror';
import ClientPreviewCard from './fidelite/ClientPreviewCard';
import ProgrammeStatsCard from './fidelite/ProgrammeStatsCard';
import {
  detectGainMode,
  montantFromModeA,
  montantFromModeB,
  formatGainPhrase,
  formatDynamicExample,
  buildSafeProgrammePutPayload,
  validationMessage,
  EXAMPLE_ORDER_EUR,
  buildClientPreviewScenario,
  pickReliableStats,
} from '../utils/fideliteGain';
import { normalizeRecompensesListResponse } from '../utils/recompensePresentation';
import { buildRecompenseProgrammeMirror } from '../utils/recompenseProgrammeMirror';

const RECOMPENSE_TYPES = [
  { value: '', label: '— Aucune —' },
  { value: 'MENU_OFFERT', label: 'Menu offert' },
  { value: 'BOISSON_OFFERTE', label: 'Boisson offerte' },
  { value: 'DESSERT_OFFERT', label: 'Dessert offert' },
  { value: 'REDUCTION_POURCENT', label: 'Réduction en %' },
];

/**
 * F1 + F2.2c — Mon programme : identité / gain éditables ;
 * récompenses = miroir lecture seule (catalogue F2).
 */
const ProgrammeFidelite = ({ commercantId, onManageRecompenses }) => {
  const { toasts, toast, removeToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [baselineConfig, setBaselineConfig] = useState(null);

  const [actif, setActif] = useState(false);
  const [nomProgramme, setNomProgramme] = useState('');
  const [messageBienvenue, setMessageBienvenue] = useState('');
  const [gainMode, setGainMode] = useState('A');
  const [pointsPerEuro, setPointsPerEuro] = useState('1');
  const [eurosPerPoint, setEurosPerPoint] = useState('1');
  const [gainError, setGainError] = useState('');

  const [catalogueItems, setCatalogueItems] = useState([]);
  const [catalogueLoading, setCatalogueLoading] = useState(true);
  const [catalogueError, setCatalogueError] = useState(false);

  const [recompensesAutomatiquesActif, setRecompensesAutomatiquesActif] = useState(true);
  const [recompenseNbCommandes, setRecompenseNbCommandes] = useState('');
  const [recompenseType, setRecompenseType] = useState('');
  const [recompenseValeur, setRecompenseValeur] = useState('');
  const [recompenseDescription, setRecompenseDescription] = useState('');
  // UX-C — Points Clickorico hors UI ; préservés via baselineConfig au PUT.
  // Seuils VIP/Fidèle/Nouveau/Actif : absents de l’UI ; préservés via baselineConfig au PUT
  const [legacyError, setLegacyError] = useState('');
  const [showLegacyParams, setShowLegacyParams] = useState(false);
  const [editingParams, setEditingParams] = useState(false);

  const [statsBanner, setStatsBanner] = useState(null);

  const applyBaselineToForm = useCallback((programme) => {
    if (!programme) return;
    setBaselineConfig(programme);
    setActif(!!programme.actif);
    setNomProgramme(programme.nomProgramme || '');
    setMessageBienvenue(programme.messageBienvenue || '');
    const detected = detectGainMode(programme.montantPourUnPoint ?? 1);
    setGainMode(detected.mode);
    if (detected.mode === 'A') {
      setPointsPerEuro(String(detected.points));
      setEurosPerPoint('1');
    } else {
      setEurosPerPoint(String(detected.euros));
      setPointsPerEuro('1');
    }
    setGainError('');

    setRecompensesAutomatiquesActif(
      programme.recompensesAutomatiquesActif !== undefined
        ? !!programme.recompensesAutomatiquesActif
        : true
    );
    setRecompenseNbCommandes(
      programme.recompenseNbCommandes != null ? String(programme.recompenseNbCommandes) : ''
    );
    setRecompenseType(programme.recompenseType || '');
    setRecompenseValeur(
      programme.recompenseValeur != null ? String(programme.recompenseValeur) : ''
    );
    setRecompenseDescription(programme.recompenseDescription || '');
    setLegacyError('');
  }, []);

  const loadStats = useCallback(async () => {
    if (!commercantId) return;
    try {
      const response = await apiService.request(`/fidelite/stats/${commercantId}`);
      const statsData = response?.data || response;
      setStatsBanner(pickReliableStats(statsData));
    } catch (error) {
      console.error('Erreur chargement stats:', error);
      setStatsBanner(null);
    }
  }, [commercantId]);

  const loadCatalogueMirror = useCallback(async () => {
    if (!commercantId) {
      setCatalogueItems([]);
      setCatalogueError(false);
      setCatalogueLoading(false);
      return;
    }
    setCatalogueLoading(true);
    setCatalogueError(false);
    try {
      const payload = await apiService.getFideliteRecompenses(commercantId);
      setCatalogueItems(normalizeRecompensesListResponse(payload));
      setCatalogueError(false);
    } catch (error) {
      console.error('Erreur chargement catalogue récompenses (miroir F1):', error);
      setCatalogueItems([]);
      setCatalogueError(true);
    } finally {
      setCatalogueLoading(false);
    }
  }, [commercantId]);

  const loadConfig = useCallback(async () => {
    if (!commercantId) return;
    try {
      setLoading(true);
      const response = await apiService.request(`/fidelite/config/${commercantId}`);
      if (response) {
        let nom = response.nomProgramme || 'Programme de fidélité';
        const legacyNomMojibakeFidelite = /fid\u00c3\u00a9lit\u00c3\u00a9/g;
        if (legacyNomMojibakeFidelite.test(nom)) {
          nom = nom.replace(legacyNomMojibakeFidelite, 'fidélité');
        }
        applyBaselineToForm({ ...response, nomProgramme: nom });
      }
      await Promise.all([loadStats(), loadCatalogueMirror()]);
    } catch (error) {
      console.error('Erreur chargement config:', error);
      const errorMessage =
        error?.response?.data?.error ||
        error?.message ||
        'Erreur lors du chargement de la configuration';
      toast.error(`Impossible de charger la configuration : ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  }, [commercantId, applyBaselineToForm, loadStats, loadCatalogueMirror, toast]);

  useEffect(() => {
    if (!commercantId) {
      toast.error('Erreur : identifiant commerçant manquant');
      setLoading(false);
      return;
    }
    loadConfig();
  }, [commercantId]); // eslint-disable-line react-hooks/exhaustive-deps -- charge initiale

  const rewardMirror = useMemo(
    () =>
      buildRecompenseProgrammeMirror(catalogueItems, {
        loadError: catalogueError,
        loading: catalogueLoading,
      }),
    [catalogueItems, catalogueError, catalogueLoading]
  );

  const resolveMontantPourUnPoint = () => {
    if (gainMode === 'A') {
      return montantFromModeA(pointsPerEuro);
    }
    return montantFromModeB(eurosPerPoint);
  };

  const currentMontantPreview = () => {
    const resolved = resolveMontantPourUnPoint();
    if (resolved.ok) return resolved.montantPourUnPoint;
    return null;
  };

  const phraseCourante = () => {
    if (gainMode === 'A') {
      const n = Number(String(pointsPerEuro).replace(',', '.'));
      if (!Number.isFinite(n) || n < 1) return '';
      return formatGainPhrase('A', { euros: 1, points: n });
    }
    const m = Number(String(eurosPerPoint).replace(',', '.'));
    if (!Number.isFinite(m) || m < 1) return '';
    return formatGainPhrase('B', { euros: m, points: 1 });
  };

  const parseOptionalInt = (raw) => {
    if (raw === '' || raw === null || raw === undefined) return null;
    const n = parseInt(String(raw).trim(), 10);
    if (!Number.isFinite(n)) return { error: 'nan' };
    return n;
  };

  const parseOptionalFloat = (raw) => {
    if (raw === '' || raw === null || raw === undefined) return null;
    const n = Number(String(raw).trim().replace(',', '.'));
    if (!Number.isFinite(n)) return { error: 'nan' };
    return n;
  };

  const resolveLegacyPatch = () => {
    const nbRaw = parseOptionalInt(recompenseNbCommandes);
    if (nbRaw && typeof nbRaw === 'object' && nbRaw.error) {
      return { ok: false, reason: 'nan' };
    }
    const type = recompenseType || null;
    const hasNb = nbRaw !== null;
    const hasType = !!type;
    if (hasNb !== hasType) {
      return { ok: false, reason: 'partial_n_cmd' };
    }
    if (hasNb && nbRaw < 1) {
      return { ok: false, reason: 'min' };
    }

    let valeur = parseOptionalFloat(recompenseValeur);
    if (valeur && typeof valeur === 'object' && valeur.error) {
      return { ok: false, reason: 'nan' };
    }

    // UX-C — Clickorico : echo strict baseline (aucune édition boutique).
    const accepteClickorico = !!baselineConfig?.acceptePointsClickorico;
    const conversionClickorico =
      baselineConfig?.conversionPointsClickorico != null
        ? Number(baselineConfig.conversionPointsClickorico)
        : 5;

    // Seuils : volontairement absents du patch → baselineConfig les renvoie inchangés
    return {
      ok: true,
      patch: {
        recompensesAutomatiquesActif,
        recompenseNbCommandes: hasNb ? nbRaw : null,
        recompenseType: hasType ? type : null,
        recompenseValeur: valeur,
        recompenseDescription: recompenseDescription.trim() || null,
        acceptePointsClickorico: accepteClickorico,
        conversionPointsClickorico: conversionClickorico,
      },
    };
  };

  const handleCancel = () => {
    if (!baselineConfig) return;
    applyBaselineToForm(baselineConfig);
    setEditingParams(false);
    toast.info('Modifications annulées');
  };

  const handleSave = async () => {
    if (!commercantId) {
      toast.error('Erreur : identifiant commerçant manquant');
      return;
    }
    if (!baselineConfig) {
      toast.error('Configuration non chargée');
      return;
    }

    const nom = (nomProgramme || '').trim();
    if (!nom) {
      toast.error('Le nom du programme est requis.');
      return;
    }

    const resolved = resolveMontantPourUnPoint();
    if (!resolved.ok) {
      const msg = validationMessage(resolved.reason);
      setGainError(msg);
      toast.error(msg);
      return;
    }
    setGainError('');

    const legacy = resolveLegacyPatch();
    if (!legacy.ok) {
      const msg =
        legacy.reason === 'partial_n_cmd'
          ? 'Indiquez à la fois le nombre de commandes et le type de récompense, ou laissez les deux vides.'
          : validationMessage(legacy.reason);
      setLegacyError(msg);
      setShowLegacyParams(true);
      toast.error(msg);
      return;
    }
    setLegacyError('');

    // F2.2c — ne jamais patcher pointsPourReduction / valeurReduction
    const payload = buildSafeProgrammePutPayload(baselineConfig, {
      actif,
      nomProgramme: nom,
      messageBienvenue: messageBienvenue || null,
      montantPourUnPoint: resolved.montantPourUnPoint,
      ...legacy.patch,
    });

    try {
      setSaving(true);
      const saved = await apiService.request(`/fidelite/config/${commercantId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      applyBaselineToForm(saved || { ...baselineConfig, ...payload });
      await loadStats();
      await loadCatalogueMirror();
      setEditingParams(false);
      toast.success('Configuration enregistrée avec succès !');
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Erreur lors de la sauvegarde';
      if (error?.response?.status === 403) {
        toast.error('Accès interdit : vous ne pouvez modifier que votre propre configuration');
      } else if (error?.response?.status === 400) {
        toast.error(`Données invalides : ${errorMessage}`);
      } else {
        toast.error(`Erreur lors de la sauvegarde : ${errorMessage}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const clientPreview = useMemo(
    () =>
      buildClientPreviewScenario({
        actif,
        nomProgramme,
        messageBienvenue,
        gainMode,
        pointsPerEuro,
        eurosPerPoint,
        rewardMirror,
      }),
    [
      actif,
      nomProgramme,
      messageBienvenue,
      gainMode,
      pointsPerEuro,
      eurosPerPoint,
      rewardMirror,
    ]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  const montantPreview = currentMontantPreview();
  const exemple = montantPreview
    ? formatDynamicExample(montantPreview, EXAMPLE_ORDER_EUR)
    : '';
  const gainHeadline = phraseCourante() || 'Règle de gain à définir';
  const messagePreview =
    (messageBienvenue || '').trim() || 'Aucun message client pour le moment.';

  return (
    <div className="space-y-4" data-testid="programme-fidelite-ux">
      {toasts.map((toastItem) => (
        <Toast key={toastItem.id} toast={toastItem} onClose={() => removeToast(toastItem.id)} />
      ))}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
        <div className="lg:col-span-7 xl:col-span-8 space-y-4">
          <ProgrammeHeroCard
            actif={actif}
            onActifChange={setActif}
            gainHeadline={gainHeadline}
            exemple={!editingParams ? exemple : ''}
          />

          <RecompenseProgrammeMirror
            mirror={rewardMirror}
            onManageRecompenses={onManageRecompenses}
          />

          <div
            className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3"
            data-testid="programme-params-summary"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <h3 className="text-base font-semibold text-gray-900">Paramètres du programme</h3>
              {!editingParams && (
                <button
                  type="button"
                  onClick={() => setEditingParams(true)}
                  className="px-3.5 py-1.5 rounded-lg text-sm font-medium border border-[#4A148C] text-[#4A148C] hover:bg-purple-50"
                  data-testid="programme-edit-params"
                >
                  Modifier
                </button>
              )}
            </div>
            {!editingParams && (
              <dl className="grid grid-cols-1 gap-3 text-sm">
                <div>
                  <dt className="text-gray-500">Nom</dt>
                  <dd className="font-medium text-gray-900 mt-0.5">{nomProgramme || '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Message client</dt>
                  <dd className="font-medium text-gray-900 mt-0.5 line-clamp-2 whitespace-pre-wrap">
                    {messagePreview}
                  </dd>
                </div>
              </dl>
            )}
          </div>

          {editingParams && (
            <div
              className="rounded-2xl border border-purple-200 bg-white p-4 sm:p-5 space-y-5"
              data-testid="programme-params-form"
            >
              <div className="space-y-4">
                <h3 className="text-base font-semibold text-gray-900">Identité du programme</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom du programme
                  </label>
                  <input
                    type="text"
                    value={nomProgramme}
                    onChange={(e) => setNomProgramme(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A148C] focus:border-transparent"
                    placeholder="Programme de fidélité"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message aux clients (optionnel)
                  </label>
                  <textarea
                    value={messageBienvenue}
                    onChange={(e) => setMessageBienvenue(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A148C] focus:border-transparent"
                    rows={3}
                    placeholder="Bienvenue ! Gagnez des points à chaque commande…"
                  />
                </div>
              </div>

              <div className="space-y-4 border-t border-gray-100 pt-5">
                <h3 className="text-base font-semibold text-gray-900">
                  Comment on gagne des points
                </h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setGainMode('A');
                      setGainError('');
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${
                      gainMode === 'A'
                        ? 'bg-[#4A148C] text-white border-[#4A148C]'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Points pour chaque euro
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setGainMode('B');
                      setGainError('');
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${
                      gainMode === 'B'
                        ? 'bg-[#4A148C] text-white border-[#4A148C]'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Euros pour 1 point
                  </button>
                </div>

                {gainMode === 'A' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pour 1 € dépensé, le client gagne
                    </label>
                    <div className="flex items-center gap-2 max-w-xs">
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={pointsPerEuro}
                        onChange={(e) => {
                          setPointsPerEuro(e.target.value);
                          setGainError('');
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A148C] focus:border-transparent"
                      />
                      <span className="text-sm text-gray-600 whitespace-nowrap">point(s)</span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pour combien d&apos;euros dépensés le client gagne-t-il 1 point ?
                    </label>
                    <div className="flex items-center gap-2 max-w-xs">
                      <input
                        type="number"
                        min={1}
                        step="0.01"
                        value={eurosPerPoint}
                        onChange={(e) => {
                          setEurosPerPoint(e.target.value);
                          setGainError('');
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A148C] focus:border-transparent"
                      />
                      <span className="text-sm text-gray-600">€</span>
                    </div>
                  </div>
                )}

                {phraseCourante() && (
                  <p className="text-sm font-medium text-gray-900">{phraseCourante()}</p>
                )}
                {exemple && <p className="text-sm text-gray-600">{exemple}</p>}
                {gainError && <p className="text-sm text-red-600">{gainError}</p>}
                <p className="text-xs text-gray-500">Les points ne valent que dans votre boutique.</p>
              </div>
            </div>
          )}
        </div>

        <aside className="lg:col-span-5 xl:col-span-4 space-y-4 lg:sticky lg:top-4">
          <ClientPreviewCard preview={clientPreview} />
          <ProgrammeStatsCard stats={statsBanner} />
        </aside>
      </div>

      {/*
        UX-C — surface legacy temporaire (N-commandes uniquement).
        Points Clickorico : hors UI boutique ; PUT echo baseline
        (acceptePointsClickorico / conversionPointsClickorico inchangés).
      */}
      <div
        className="rounded-xl border border-dashed border-amber-200 bg-amber-50/40 px-4 py-3"
        data-testid="programme-legacy-ncmd-panel"
      >
        <button
          type="button"
          onClick={() => setShowLegacyParams((v) => !v)}
          className="w-full flex items-center justify-between text-left"
          data-testid="programme-legacy-ncmd-toggle"
        >
          <div>
            <h3 className="text-sm font-medium text-amber-950">Ancienne règle de fidélité</h3>
            <p className="text-xs text-amber-900/70 mt-0.5">
              Configuration historique conservée temporairement — distincte du catalogue
              Récompenses (échange de points).
            </p>
          </div>
          <span className="text-sm text-[#4A148C] font-medium ml-4 whitespace-nowrap">
            {showLegacyParams ? 'Masquer' : 'Afficher'}
          </span>
        </button>

        {showLegacyParams && (
          <div className="mt-5 space-y-4 border-t border-amber-200/80 pt-5">
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900 text-sm">
                Récompense après un nombre de commandes
              </h4>
              <p className="text-xs text-gray-600">
                Ancien automatisme boutique (jalon de commandes). Ce n&apos;est pas une
                récompense du catalogue F2 : le client n&apos;échange pas ses points ici.
              </p>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={recompensesAutomatiquesActif}
                  onChange={(e) => setRecompensesAutomatiquesActif(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">Activer cette ancienne règle</span>
              </label>
              {recompensesAutomatiquesActif && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre de commandes
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={recompenseNbCommandes}
                      onChange={(e) => setRecompenseNbCommandes(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                      placeholder="Ex. 10"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                      value={recompenseType}
                      onChange={(e) => setRecompenseType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                    >
                      {RECOMPENSE_TYPES.map((opt) => (
                        <option key={opt.value || 'empty'} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {recompenseType === 'REDUCTION_POURCENT' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Valeur (%)
                      </label>
                      <input
                        type="number"
                        min={0}
                        step="0.1"
                        value={recompenseValeur}
                        onChange={(e) => setRecompenseValeur(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                      />
                    </div>
                  )}
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <input
                      type="text"
                      value={recompenseDescription}
                      onChange={(e) => setRecompenseDescription(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                    />
                  </div>
                </div>
              )}
            </div>

            {legacyError && <p className="text-sm text-red-600">{legacyError}</p>}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 sticky bottom-0 bg-gradient-to-t from-white via-white to-transparent py-3">
        <button
          type="button"
          onClick={handleCancel}
          disabled={saving}
          className="px-5 py-2.5 bg-white border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 disabled:opacity-50 transition"
        >
          Annuler
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="px-5 py-2.5 bg-[#4A148C] text-white font-semibold rounded-lg hover:bg-[#6A1B9A] disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>
    </div>
  );
};


export default ProgrammeFidelite;
